import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import { type FormEvent, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InsuredRiskFields } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/InsuredRiskFields";
import {
	getInsuredPersonSchema,
	INSURED_RISK_DEFAULT_VALUES,
	type InsuredPersonInput,
	type InsuredPersonOutput,
} from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/insuredPersonSchema";
import { useInsuranceRiskOptions } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/InsuredPersons/useInsuranceRiskOptions";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type Props = {
	open: boolean;
	onClose: () => void;
	defaultValues: InsuredPersonInput;
	takenIdentificationNumbers: string[];
	onSave: (person: InsuredPersonInput) => void;
	onRemove?: () => void;
};

const PageResources = Resources.Portfolio.AddLifeInsurance;

export const InsuredPersonDrawer = ({
	open,
	onClose,
	defaultValues,
	takenIdentificationNumbers,
	onSave,
	onRemove,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const riskOptions = useInsuranceRiskOptions();

	const form = useForm<InsuredPersonInput, unknown, InsuredPersonOutput>({
		resolver: zodResolver(
			getInsuredPersonSchema(t, takenIdentificationNumbers),
		),
		defaultValues,
		mode: "onChange",
	});

	const risks = useFieldArray({ control: form.control, name: "risks" });

	useEffect(() => {
		if (open) {
			form.reset(defaultValues);
		}
	}, [open, defaultValues, form.reset]);

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		void form.handleSubmit(() => {
			onSave(form.getValues());
			onClose();
		})(event);
	};

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(PageResources.InsuredPersons.drawerTitle)}
			grabber
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<Stack bgcolor={theme.palette.background.paper}>
					<FlatRowFormInput
						control={form.control}
						name="firstname"
						label={t(PageResources.InsuredPersons.firstname)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					/>
					<FlatRowFormInput
						control={form.control}
						name="lastname"
						label={t(PageResources.InsuredPersons.lastname)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					/>
					<FlatRowFormInput
						control={form.control}
						name="identificationNumber"
						label={t(PageResources.InsuredPersons.identificationNumber)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						isLast
					/>
				</Stack>

				<Typography variant="sectionLabel">
					{t(PageResources.InsuredPersons.risksTitle)}
				</Typography>

				<Stack bgcolor={theme.palette.background.paper}>
					{risks.fields.map((field, index) => (
						<InsuredRiskFields
							key={field.id}
							form={form}
							index={index}
							riskOptions={riskOptions}
							onRemove={() => risks.remove(index)}
						/>
					))}
					<AddRowButton
						label={t(PageResources.InsuredPersons.addRisk)}
						onClick={() => risks.append(INSURED_RISK_DEFAULT_VALUES)}
					/>
				</Stack>

				<Stack
					px={theme.spacingTokens.lg}
					pt={theme.spacingTokens.lg}
					gap={theme.spacingTokens.sm}
				>
					<Button type="submit" variant="primary">
						{t(PageResources.InsuredPersons.savePerson)}
					</Button>
					{onRemove && (
						<Stack alignItems="center" pt={theme.spacingTokens.md}>
							<Button
								variant="textLink"
								color="error"
								startIcon={<CircleMinus size={theme.iconSize.sm} />}
								onClick={() => {
									onRemove();
									onClose();
								}}
							>
								{t(PageResources.InsuredPersons.removePerson)}
							</Button>
						</Stack>
					)}
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
